import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { updateCell } from '@/redux/slices/payoutStructure';
import styles from '../payout-structures.module.css';

interface TableCellProps {
  rowIndex: number;
  columnIndex: number;
}

const TableCell: React.FC<TableCellProps> = ({ rowIndex, columnIndex }) => {
  const dispatch = useDispatch();

  // Get the cell value from Redux
  const cellValue = useSelector((state: any) => {
    const column = state.payoutStructure.playersColumns[columnIndex];
    return column?.percentages[rowIndex] ?? 0;
  });

  const [localValue, setLocalValue] = useState(cellValue.toString());
  const [isFocused, setIsFocused] = useState(false);

  // Sync local value with Redux when not focused
  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(cellValue.toString());
    }
  }, [cellValue, isFocused]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // Prevent more than 5 decimal places
      if (value.split('.')[1]?.length > 5) {
        return;
      }

      setLocalValue(value);

      // Parse and validate the value for dispatching
      const numValue = parseFloat(value);

      // Only dispatch if it's a valid number
      if (!isNaN(numValue)) {
        // Clamp value between 0 and 100
        const clampedValue = Math.max(0, Math.min(100, numValue));
        dispatch(updateCell({ rowIndex, columnIndex, value: clampedValue }));
      } else if (value === '' || value === '0') {
        // Handle empty string or zero
        dispatch(updateCell({ rowIndex, columnIndex, value: 0 }));
      }
    },
    [dispatch, rowIndex, columnIndex],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    let numValue = parseFloat(localValue) || 0;

    // Clamp value between 0 and 100
    numValue = Math.max(0, Math.min(100, numValue));

    // Update local value to match the clamped value for display consistency
    setLocalValue(numValue.toString());

    // Ensure the Redux store has the final clamped value
    if (numValue !== cellValue) {
      dispatch(updateCell({ rowIndex, columnIndex, value: numValue }));
    }
  }, [dispatch, localValue, cellValue, rowIndex, columnIndex]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  }, []);

  return (
    <div className={styles.tableCellContainer}>
      <input
        className={classNames(
          styles.payoutStructureTableInputCell,
          Number(cellValue) === 0 && styles.payoutStructureTableInputCellEmpty,
          {
            [styles.oddColumn]: columnIndex % 2 === 0,
          },
        )}
        type="number"
        min={0}
        max={100}
        value={localValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

TableCell.displayName = 'TableCell';

export default TableCell;
